import { Button, Stack, Typography } from "@mui/material";
import StatusChip from "./StatusChip";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useUserInfoQuery } from "../../../api/userApiSlice";
import { useGetDUCInfoQuery } from "../../../api/ducApiSlice";
import { getRenewalStatus } from "../utils/renewalWindow";


const AIANBlockButtons = (props: any) => {
    const navigate = useNavigate();
    const { data: userData } = useUserInfoQuery({});
    const { data: ducDataObject } = useGetDUCInfoQuery({ email: userData?.email });
    const ducData = ducDataObject?.AIAN?.[ducDataObject?.AIAN.length - 1]
    const secondDucExists = ducDataObject?.AIAN.length === 2

    // Check if any General DUC has Approved status
    const hasApprovedGeneral = ducDataObject?.GENERAL?.some((gen: { status: string }) => gen.status === 'Approved') || false

    // AIAN-specific expired flag from the latest AIAN record (BE can mark it as DucExpired)
    const isAianDucExpired: boolean = ducData?.status === 'DucExpired'

    const DucNotExpiringAndNotExpiredAIAN: boolean = !props.expired && !props.expiring && !isAianDucExpired
    const DucExpiringNotExipiredAIAN: boolean = props.expiring && !props.expired
    const DucInProgressNotSignedByOfficialAIAN: boolean = (ducData?.status === "InProgress" || ducData?.status === "Submitted") && !ducData?.dateSignedOfficial
    const notSubApplicantGen: boolean = !ducDataObject?.subApplicantGeneral && ducDataObject?.subApplicantGeneral !== undefined
    const notSubApplicant: boolean = !ducDataObject?.subApplicantAIAN && ducDataObject?.subApplicantAIAN !== undefined
    const DucInProgressAIAN: boolean = ducData?.status !== "NotSubmitted" && ducData?.status !== "Approved" && ducData?.status !== "Declined"
    const DucApprovedTrainingCompleteAIAN: boolean = ducData?.status === "Approved" && ducDataObject?.ducStatus === "TrainingComplete"
    const DucApprovedOrDeclinedAndTrainingCompleteAIAN: boolean = (ducData?.status === "Approved" || ducData?.status === "Declined") && ducDataObject?.ducStatus === "TrainingComplete"
    const DucNotDeclinedOrSecondDucDoesExistAIAN: boolean = (ducData?.status !== 'Declined' || secondDucExists)

    // Block AIAN actions if General DUC training expired or DUC expired per BE status,
    // and also when General DUC is considered expired by UI (renewal window)
    const isAianBlockedByTraining: boolean = ducDataObject?.ducStatus === 'TrainingExpired'
    const isAianBlockedByDuc: boolean = ducDataObject?.ducStatus === 'DucExpired'

    // Determine UI-expired/expiring state for the latest General DUC using unified renewal logic.
    // AIAN renewal must never be started before General is renewed - AIAN's own expiry always
    // mirrors General's (see backend WebhookService), so renewing AIAN first would just inherit
    // General's still-soon-to-expire date and gain nothing, and once General IS renewed the
    // backend hard-expires the PI's current AIAN DUC anyway. So AIAN renewal stays blocked for as
    // long as General itself is due for renewal (already expired OR merely expiring soon) -
    // not just once General has fully expired.
    const generalDuc = ducDataObject?.GENERAL?.[ducDataObject?.GENERAL.length - 1]
    const { expired: isGeneralExpiredUI, expiring: isGeneralExpiringUI } = getRenewalStatus({
        status: generalDuc?.status,
        expiresOn: generalDuc?.expiresOn,
        isSub: !!ducDataObject?.subApplicantGeneral
    })
    const isGeneralDueForRenewalUI: boolean = isGeneralExpiredUI || isGeneralExpiringUI

    const isAianDisabled: boolean = isAianBlockedByTraining || isAianBlockedByDuc || isGeneralDueForRenewalUI


    const setOldStatus = () => {
        localStorage.setItem('status', ducData?.status)
        // Clear the one-shot renewal flag so it can't leak into unrelated flows.
        // handleClickAianRenewal re-sets it AFTER this call when the special
        // "sub-applicant with DucExpired starts a renewal" case applies.
        localStorage.removeItem('skipPrevData')
    }

    const handleClickAian = () => {
        setOldStatus()
        localStorage.setItem('edit', "false")
        localStorage.setItem('formType', "AIAN")
        if((ducData?.status === 'NotSubmitted' || ducData?.status === 'Declined') && ducDataObject?.group){
            props.setOpen(true)
        }
        else{
            navigate("/duc/request");
        }
    }

    const handleClickAianView = async () => {
        setOldStatus()
        localStorage.setItem('edit', "false")
        localStorage.setItem('formType', "AIAN")
        // If AIAN DUC is Approved, open the finalized certification/PDF in a new tab
        if (ducData?.status === 'Approved' && ducData?.ducDetailsId) {
            try {
                const response = await fetch(`/api/duc/file/${ducData.ducDetailsId}`, { credentials: 'include' })
                if (!response.ok) throw new Error('Failed to retrieve certification')
                const json = await response.json()
                const base64 = json?.file
                if (!base64) throw new Error('Certification file is empty')

                const binaryString = atob(base64)
                const len = binaryString.length
                const bytes = new Uint8Array(len)
                for (let i = 0; i < len; i++) {
                    bytes[i] = binaryString.charCodeAt(i)
                }
                const blob = new Blob([bytes], { type: 'application/pdf' })
                const url = URL.createObjectURL(blob)
                window.open(url, '_blank')
                return
            } catch (e) {
                console.error('View Certification (AIAN) failed:', e)
            }
        }
        // Default behavior (non-Approved statuses): open the request viewer at Review
        localStorage.setItem('viewCertification', 'true')
        navigate("/duc/request");
    }

    const handleClickEdit = () => {
        setOldStatus()
        if(ducData?.group){
            localStorage.setItem('isGroup', "true")
        }
        else{
            localStorage.setItem('isGroup', "false")
        }
        localStorage.setItem('formType', "AIAN")
        localStorage.setItem('AddSub', "false")
        localStorage.setItem('edit', "true")
        navigate("/duc/request");
    }

    const handleClickViewSub = () => {
        setOldStatus()
        navigate("/duc/sub-applicants",{ state: { "type": "AIAN" } })
    }

    const handelCLickAddSub = () => {
        setOldStatus()
        localStorage.setItem('formType', "AIAN")
        localStorage.setItem('AddSub', "true")
        localStorage.setItem('edit', "true")
        localStorage.setItem('isGroup', "true")
        navigate("/duc/request")
    }

    const handleClickDelete = () => {
        setOldStatus()
        props.setCancelRefresh(!props.cancelRefresh)
        props.setOpenDeleteModal(true);
    }

    // Renewal: mirror General behavior — always open the selection modal (Individual vs Group)
    // and set flags so the process treats this flow as a Renewal.
    const handleClickAianRenewal = () => {
        setOldStatus()
        localStorage.setItem('edit', "false")
        localStorage.setItem('formType', "AIAN")
        localStorage.setItem('AddSub', "false")
        // One-shot signal to treat the upcoming flow as a Renewal (used by DUCProcess hook)
        localStorage.setItem('forceRenewal', "true")

        // Special case: if the user is a sub-applicant and their General/overall DUC status is DucExpired,
        // treat renewal as a fresh request and do NOT pull old details.
        const isSubAian = !!ducDataObject?.subApplicantAIAN
        const isDucExpired = ducDataObject?.ducStatus === 'DucExpired'
        if (isSubAian && isDucExpired) {
            localStorage.setItem('skipPrevData', "true")
            props.setOpen(true)
            return
        } else {
            // For all other cases, ensure the special flag is cleared and show the selection modal
            localStorage.removeItem('skipPrevData')
        }

        // Do NOT set isGroup here; let the selection modal set it based on user choice
        props.setOpen(true)
    }

    return (
        <Stack
            flexDirection="row"
            justifyContent="space-between"
            gap={3}>
            <Stack
                flexDirection="row"
                gap={2}
                alignItems="center"
                maxWidth={"600px"}>
                <Typography fontSize="16px" fontWeight="600" variant="h2" id="aian-header">
                Data Use Certification for Research on NIH Brain Development Cohorts Data from American Indian/Alaska Native Individuals (A-DUC)
                </Typography>

                {ducData?.status  === 'Approved' && DucNotExpiringAndNotExpiredAIAN &&
                <StatusChip
                    state="approved"/>}

                {DucExpiringNotExipiredAIAN &&
                <StatusChip
                    state='expiring'
                    time={getRenewalStatus({ status: ducData?.status, expiresOn: ducData?.expiresOn, isSub: !!ducDataObject?.subApplicantAIAN }).daysLeft}/>}

                {(isAianBlockedByTraining || isAianBlockedByDuc || isGeneralDueForRenewalUI) && (
                    <Typography fontSize="11px" fontWeight="600" color={isAianBlockedByTraining ? 'warning.main' : 'error.main'}>
                        {isAianBlockedByTraining
                            ? 'Start training to enable A-DUC application.'
                            : 'Renew your General DUC to enable A-DUC application.'}
                    </Typography>
                )}

                {props.expired &&
                <StatusChip
                    state='expired'/>}
            </Stack>



            {ducData?.status === 'NotSubmitted' &&
            <Stack
                flexDirection="row">
                {!hasApprovedGeneral &&
                    <Typography
                        fontSize="11px"
                        fontWeight="600"
                        marginRight="20px">
                        An approved general data use certification is required
                    </Typography>
                }
                {notSubApplicantGen &&
                    <Button
                        id="duc-aian-request-access-button"
                        variant="primary"
                        onClick={handleClickAian}
                        sx={{ alignSelf: "center", minWidth: "max-content" }}
                        disabled={!hasApprovedGeneral || isAianDisabled}
                    >
                        Request access
                    </Button>
                }
                {!notSubApplicantGen &&
                    <Stack>
                        <Typography fontSize="14px" fontWeight="600">
                            Sub-applicants must ask their PI or Lead Investigator to submit the request.
                        </Typography>
                    </Stack>
                }
            </Stack>}

            {ducData?.status === 'Declined' && !secondDucExists && !isAianDucExpired &&
            <Button
                id="duc-aian-new-request-button"
                variant="primary"
                onClick={handleClickAian}
                sx={{ alignSelf: "center" }}
            >
                New Request
            </Button>}

            {ducData?.status !== 'NotSubmitted' && DucNotDeclinedOrSecondDucDoesExistAIAN && (DucNotExpiringAndNotExpiredAIAN || (props.expiring && !props.expired) || (ducData?.status === 'Submitted' && isAianDucExpired) || (ducData?.status === 'InProgress' && isAianDucExpired)) && props.checkedCurrentStatus &&
            <Stack
                flexDirection="row"
                gap={4}
                alignItems={'center'}>
                <Button
                    id="duc-aian-view-request-button"
                    variant="primary"
                    onClick={handleClickAianView}
                    sx={{ minWidth: "max-content" }}
                >
                    {(ducData?.status !== "Approved") ? "View Request": "View Certification"}
                </Button>

                {DucInProgressNotSignedByOfficialAIAN && notSubApplicant &&
                    <Button
                        id="duc-aian-edit-request-button"
                        variant="secondary"
                        onClick={handleClickEdit}
                        sx={{ minWidth: "max-content" }}
                        disabled={isAianDisabled}
                    >
                        Edit Request
                    </Button>}

                {DucInProgressAIAN && notSubApplicant &&
                    <Button
                        id="duc-aian-cancel-button"
                        variant="outlined"
                        color="error"
                        sx={{ minWidth: "max-content" }}
                        onClick={handleClickDelete}
                        disabled={isAianDisabled}
                    >
                        Cancel
                    </Button>
                }
                {(DucApprovedTrainingCompleteAIAN || secondDucExists) && ducData.group && notSubApplicant &&
                    <Button
                        id="duc-aian-view-subapplicants-button"
                        variant="primary"
                        onClick={handleClickViewSub}
                        sx={{ minWidth: "max-content" }}
                    >
                        View Sub-applicant(s)
                    </Button>}

                {DucApprovedOrDeclinedAndTrainingCompleteAIAN && ducData.group && notSubApplicant &&
                    <Button
                        sx={{ minWidth: "max-content" }}
                        id="duc-aian-add-subapplicant-button"
                        variant="primary"
                        startIcon={<AddBoxOutlinedIcon/>}
                        onClick={handelCLickAddSub}
                        disabled={isAianDisabled}
                    >
                        Add new Sub-applicant
                    </Button>}

            </Stack>}

            {/* Renewal is a PI/Lead-Investigator action only - Sub-applicants must never see it,
                regardless of isAianDisabled (which only ever disables, never hides, the button). */}
            {notSubApplicant && ((props.expiring || props.expired || isAianDucExpired) && !(ducData?.status === 'Submitted' && isAianDucExpired) && !(ducData?.status === 'InProgress' && isAianDucExpired)) &&
            <Button
                id="duc-aian-renewal-button"
                variant="primary"
                onClick={handleClickAianRenewal}
                sx={{ alignSelf: "center",
                    marginLeft: "75px",
                }}
                disabled={isAianDisabled}
            >
                Renewal
            </Button>}

        </Stack>
    )
}

export default AIANBlockButtons
